import React, { useState, useEffect, useRef } from "react";
import { Result } from "../../types";
import PDFExport from "./WorkplacePDFExport";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { Bar, Doughnut, Radar } from "react-chartjs-2";
import {
  Computer,
  Hub,
  CloudSync,
  SmartToy,
  Security,
  Analytics,
  Architecture,
  PersonAdd,
  AttachMoney,
  Speed,
  Assignment,
  Celebration,
  Timeline,
  TrendingUp,
  Business,
  Groups,
  Settings,
  Dashboard,
  Insights,
} from "@mui/icons-material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler
);

interface ResultsPageProps {
  results: Result[];
  onRestart: () => void;
}

const WorkplaceResults: React.FC<ResultsPageProps> = ({
  results,
  onRestart,
}) => {
  const [animationStep, setAnimationStep] = useState(0);
  const [selectedTab, setSelectedTab] = useState("recommendations");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimationStep((prev) => (prev < 4 ? prev + 1 : prev));
    }, 500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Enhanced Digital Workplace recommendations
  const generateDetailedRecommendations = () => {
    const recommendations = [];
    const answers = results.map((r) => r.answer.toLowerCase());

    const hasCollaborationNeed = answers.some(
      (a) =>
        a.includes("email") || a.includes("scattered") || a.includes("meetings")
    );
    const hasAutomationNeed = answers.some(
      (a) => a.includes("manual") || a.includes("repetitive")
    );
    const hasRemoteWorkNeed = answers.some(
      (a) =>
        a.includes("office") || a.includes("limited") || a.includes("remotely")
    );
    const hasDataOrganizationNeed = answers.some(
      (a) =>
        a.includes("chaos") ||
        a.includes("poorly organized") ||
        a.includes("searching")
    );
    const hasSecurityNeed = answers.some(
      (a) =>
        a.includes("concerned") || a.includes("gaps") || a.includes("worried")
    );
    const hasAnalyticsNeed = answers.some(
      (a) =>
        a.includes("days") ||
        a.includes("without complete") ||
        a.includes("searching")
    );

    // Core Collaboration Platform
    if (hasCollaborationNeed) {
      recommendations.push({
        category: "Collaboration",
        title: "Unified Communication Platform",
        description:
          "Integrate chat, video conferencing, file sharing, and project management into a single platform for seamless team collaboration.",
        priority: "High",
        icon: <Hub />,
        benefits: [
          "Centralized communication hub",
          "Real-time collaboration tools",
          "Integrated file sharing",
          "Cross-platform accessibility",
        ],
        implementation: "4-8 weeks",
        roi: "180% within 6 months",
        score: 95,
      });
    }

    // Process Automation
    if (hasAutomationNeed) {
      recommendations.push({
        category: "Automation",
        title: "Workflow Automation Suite",
        description:
          "Automate repetitive tasks and business processes to free up time for strategic work and reduce human error.",
        priority: "High",
        icon: <SmartToy />,
        benefits: [
          "Reduced manual work",
          "Increased accuracy",
          "Time savings",
          "Scalable processes",
        ],
        implementation: "6-12 weeks",
        roi: "220% within 4 months",
        score: 92,
      });
    }

    // Remote Work Infrastructure
    if (hasRemoteWorkNeed) {
      recommendations.push({
        category: "Remote Work",
        title: "Cloud-First Infrastructure",
        description:
          "Deploy secure, scalable cloud infrastructure that enables productive remote work from anywhere.",
        priority: "High",
        icon: <CloudSync />,
        benefits: [
          "Secure remote access",
          "Scalable cloud resources",
          "Device flexibility",
          "Global accessibility",
        ],
        implementation: "8-16 weeks",
        roi: "160% within 8 months",
        score: 88,
      });
    }

    // Knowledge Management
    if (hasDataOrganizationNeed) {
      recommendations.push({
        category: "Knowledge Management",
        title: "Intelligent Content Hub",
        description:
          "Create a centralized, AI-powered knowledge repository that makes information easily discoverable and accessible.",
        priority: "Medium",
        icon: <Computer />,
        benefits: [
          "Centralized knowledge base",
          "AI-powered search",
          "Version control",
          "Access permissions",
        ],
        implementation: "6-10 weeks",
        roi: "140% within 6 months",
        score: 85,
      });
    }

    // Security Framework
    if (hasSecurityNeed) {
      recommendations.push({
        category: "Security",
        title: "Zero-Trust Security Framework",
        description:
          "Implement comprehensive security measures including multi-factor authentication, encryption, and continuous monitoring.",
        priority: "High",
        icon: <Security />,
        benefits: [
          "Enhanced data protection",
          "Compliance readiness",
          "Threat detection",
          "User authentication",
        ],
        implementation: "8-12 weeks",
        roi: "200% within 12 months",
        score: 90,
      });
    }

    // Analytics and Insights
    if (hasAnalyticsNeed) {
      recommendations.push({
        category: "Analytics",
        title: "Business Intelligence Dashboard",
        description:
          "Real-time analytics and reporting tools that provide insights for faster, data-driven decision making.",
        priority: "Medium",
        icon: <Analytics />,
        benefits: [
          "Real-time insights",
          "Custom dashboards",
          "Predictive analytics",
          "Performance tracking",
        ],
        implementation: "4-8 weeks",
        roi: "170% within 6 months",
        score: 82,
      });
    }

    // Always include these core recommendations
    recommendations.push({
      category: "Integration",
      title: "API Integration Platform",
      description:
        "Connect all your tools and systems with a unified integration platform that eliminates data silos.",
      priority: "Medium",
      icon: <Architecture />,
      benefits: [
        "Seamless data flow",
        "Reduced manual entry",
        "System connectivity",
        "Automated workflows",
      ],
      implementation: "6-10 weeks",
      roi: "150% within 8 months",
      score: 78,
    });

    recommendations.push({
      category: "Employee Experience",
      title: "Digital Employee Onboarding",
      description:
        "Streamlined digital onboarding process that gets new employees productive faster while maintaining engagement.",
      priority: "Low",
      icon: <PersonAdd />,
      benefits: [
        "Faster onboarding",
        "Consistent experience",
        "Reduced HR workload",
        "Better retention",
      ],
      implementation: "4-6 weeks",
      roi: "130% within 12 months",
      score: 75,
    });

    return recommendations;
  };

  const recommendations = generateDetailedRecommendations();
  const categories = [...new Set(recommendations.map((r) => r.category))];

  // Chart data for workplace-specific metrics
  const workplaceEfficiencyData = {
    labels: categories,
    datasets: [
      {
        label: "Recommended Solutions",
        data: categories.map(
          (cat) => recommendations.filter((r) => r.category === cat).length
        ),
        backgroundColor: "rgba(59, 130, 246, 0.8)",
        borderColor: "#3b82f6",
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const implementationTimelineData = {
    labels: recommendations.map((r) =>
      r.title.split(" ").slice(0, 2).join(" ")
    ),
    datasets: [
      {
        label: "Implementation (weeks)",
        data: recommendations.map((r) => {
          // Extract weeks from strings like "4-8 weeks"
          const weekMatch = r.implementation?.match(/(\d+)-(\d+)/);
          return weekMatch ? parseInt(weekMatch[1]) : 4;
        }),
        backgroundColor: "rgba(34, 197, 94, 0.6)",
        borderColor: "#22c55e",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const roiComparisonData = {
    labels: recommendations.map((r) =>
      r.title.split(" ").slice(0, 2).join(" ")
    ),
    datasets: [
      {
        label: "ROI Percentage",
        data: recommendations.map((r) => {
          const roiMatch = r.roi?.match(/(\d+)%/);
          return roiMatch ? parseInt(roiMatch[1]) : 100;
        }),
        backgroundColor: [
          "#ef4444",
          "#f59e0b",
          "#10b981",
          "#3b82f6",
          "#8b5cf6",
          "#ec4899",
          "#6b7280",
          "#14b8a6",
        ],
        borderWidth: 0,
        hoverOffset: 10,
      },
    ],
  };

  const workplacePriorityData = {
    labels: recommendations.map((r) =>
      r.title.split(" ").slice(0, 2).join(" ")
    ),
    datasets: [
      {
        label: "Business Impact Score",
        data: recommendations.map((r) => r.score),
        backgroundColor: "rgba(168, 85, 247, 0.3)",
        borderColor: "#a855f7",
        pointBackgroundColor: "#a855f7",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#a855f7",
      },
    ],
  };

  return (
    <div
      ref={resultsRef}
      style={{
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        minHeight: "100vh",
        padding: isMobile ? "1rem" : "2rem",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Enhanced Header with responsive design */}
        <div
          className={animationStep >= 0 ? "fade-in" : ""}
          style={{
            textAlign: "center",
            marginBottom: isMobile ? "2rem" : "3rem",
            color: "white",
          }}
        >
          <div
            style={{
              fontSize: isMobile ? "2.5rem" : "4rem",
              marginBottom: "1rem",
            }}
          >
            <Celebration
              sx={{ fontSize: isMobile ? "2.5rem" : "4rem", color: "white" }}
            />
          </div>
          <h1
            style={{
              fontFamily: "var(--font-headline)",
              fontSize: isMobile
                ? "var(--font-size-headline-mobile)"
                : "var(--font-size-headline)",
              fontWeight: "800",
              marginBottom: "1rem",
              background:
                "linear-gradient(35deg,rgb(215, 227, 255) 0%,rgb(155, 188, 255) 30%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              lineHeight: "1.2",
              padding: isMobile ? "0 1rem" : "0",
            }}
          >
            Your Digital Workplace Solutions Roadmap
          </h1>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: isMobile
                ? "var(--font-size-body-mobile)"
                : "var(--font-size-body)",
              opacity: 0.9,
              maxWidth: "700px",
              margin: "0 auto 2rem",
              lineHeight: "1.6",
              padding: isMobile ? "0 1rem" : "0",
            }}
          >
            Based on your responses, we've created a comprehensive digital
            workplace strategy tailored to your organization's needs with
            detailed implementation guidance and ROI projections.
          </p>

          {/* Key Statistics */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "repeat(2, 1fr)"
                : "repeat(auto-fit, minmax(200px, 1fr))",
              gap: isMobile ? "0.75rem" : "1rem",
              maxWidth: "800px",
              margin: "0 auto",
              padding: isMobile ? "0 0.5rem" : "0",
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
              }}
            >
              <Assignment
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                {recommendations.length}
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                Solutions
              </div>
            </div>
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
              }}
            >
              <Speed
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                4-16 weeks
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                Implementation
              </div>
            </div>
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
                gridColumn: isMobile ? "1 / -1" : "auto",
              }}
            >
              <AttachMoney
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                170%+
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                Expected ROI within 6-12 months
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div
          className={animationStep >= 1 ? "fade-in" : ""}
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: isMobile ? "2rem" : "3rem",
            opacity: animationStep >= 1 ? 1 : 0,
            transform:
              animationStep >= 1 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-in-out",
            flexWrap: "wrap",
            gap: isMobile ? "0.5rem" : "0",
          }}
        >
          {["recommendations", "analytics", "your-responses"].map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              style={{
                padding: isMobile ? "0.75rem 1rem" : "1rem 2rem",
                margin: isMobile ? "0" : "0 0.5rem",
                backgroundColor:
                  selectedTab === tab
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(255, 255, 255, 0.1)",
                color: selectedTab === tab ? "#1f2937" : "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: isMobile ? "0.9rem" : "1.1rem",
                fontWeight: "600",
                transition: "all 0.3s ease",
                backdropFilter: "blur(10px)",
                textTransform: "capitalize",
                minWidth: isMobile ? "auto" : "120px",
              }}
            >
              {tab === "your-responses" ? "Your Responses" : tab}
            </button>
          ))}
        </div>

        {/* Recommendations Tab */}
        {selectedTab === "recommendations" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(400px, 1fr))",
                gap: isMobile ? "1.5rem" : "2rem",
                marginBottom: isMobile ? "2rem" : "3rem",
              }}
            >
              {recommendations.map((recommendation, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: "20px",
                    padding: isMobile ? "1.5rem" : "2rem",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    transform: `translateY(${index * 10}px)`,
                    animation: `slideUp 0.6s ease-out ${index * 0.1}s both`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: isMobile ? "flex-start" : "center",
                      marginBottom: "1.5rem",
                      flexDirection: isMobile ? "column" : "row",
                      textAlign: isMobile ? "center" : "left",
                    }}
                  >
                    <div
                      style={{
                        fontSize: isMobile ? "2rem" : "3rem",
                        marginRight: isMobile ? "0" : "1rem",
                        marginBottom: isMobile ? "1rem" : "0",
                        color: "#c9a750ff",
                      }}
                    >
                      {React.cloneElement(recommendation.icon, {
                        sx: { fontSize: isMobile ? "2rem" : "3rem" },
                      })}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#c9a750ff",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        {recommendation.category}
                      </div>
                      <h3
                        style={{
                          color: "#1f2937",
                          margin: 0,
                          fontSize: isMobile ? "1.2rem" : "1.4rem",
                          lineHeight: "1.3",
                        }}
                      >
                        {recommendation.title}
                      </h3>
                    </div>
                  </div>

                  <p
                    style={{
                      color: "#6b7280",
                      lineHeight: "1.6",
                      marginBottom: "1.5rem",
                      fontSize: isMobile ? "0.9rem" : "1rem",
                    }}
                  >
                    {recommendation.description}
                  </p>

                  <div style={{ marginBottom: "1.5rem" }}>
                    <h4
                      style={{
                        color: "#1f2937",
                        marginBottom: "0.75rem",
                        fontSize: "1rem",
                      }}
                    >
                      Key Benefits:
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr"
                          : "repeat(2, 1fr)",
                        gap: "0.5rem",
                      }}
                    >
                      {recommendation.benefits.map((benefit, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            fontSize: isMobile ? "0.8rem" : "0.9rem",
                            color: "#4b5563",
                          }}
                        >
                          <span
                            style={{ color: "#10b981", marginRight: "0.5rem" }}
                          >
                            ✓
                          </span>
                          {benefit}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                      gap: "1rem",
                      padding: "1rem",
                      backgroundColor: "rgba(102, 126, 234, 0.1)",
                      borderRadius: "12px",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Timeline
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#1f2937",
                        }}
                      >
                        {recommendation.implementation}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Expected ROI
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#059669",
                        }}
                      >
                        {recommendation.roi}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Impact Score
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#c9a750ff",
                        }}
                      >
                        {recommendation.score}/100
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {selectedTab === "analytics" && (
          <div
            className={`analytics-container ${
              animationStep >= 2 ? "fade-in" : ""
            }`}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                gap: isMobile ? "1.5rem" : "2rem",
                marginBottom: isMobile ? "2rem" : "3rem",
              }}
            >
              {/* Solutions by Category */}
              <div
                data-chart="workplace-solutions"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Dashboard
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Solutions by Category
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Distribution of recommended workplace solutions across
                  different areas
                </p>
                <Bar
                  data={workplaceEfficiencyData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `${context.parsed.y} recommended solutions`;
                          },
                        },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: !isMobile,
                          text: "Number of Solutions",
                        },
                        ticks: {
                          font: {
                            size: isMobile ? 10 : 12,
                          },
                        },
                      },
                      x: {
                        ticks: {
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                          maxRotation: isMobile ? 45 : 0,
                        },
                      },
                    },
                  }}
                />
              </div>

              {/* ROI Comparison */}
              <div
                data-chart="workplace-roi"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <TrendingUp
                    sx={{
                      marginRight: "0.5rem",
                      color: "#667eea",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  ROI Comparison
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Expected return on investment for each solution
                </p>
                <Doughnut
                  data={roiComparisonData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: {
                          usePointStyle: true,
                          padding: isMobile ? 10 : 20,
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `${context.label}: ${context.parsed}% ROI`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>

              {/* Implementation Timeline */}
              <div
                data-chart="workplace-timeline"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Timeline
                    sx={{
                      marginRight: "0.5rem",
                      color: "#667eea",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Implementation Timeline
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Estimated implementation time for each solution
                </p>
                <Bar
                  data={implementationTimelineData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `${context.parsed.y} weeks minimum`;
                          },
                        },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: !isMobile,
                          text: "Weeks",
                        },
                        ticks: {
                          font: {
                            size: isMobile ? 10 : 12,
                          },
                        },
                      },
                      x: {
                        ticks: {
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                          maxRotation: isMobile ? 45 : 0,
                        },
                      },
                    },
                  }}
                />
              </div>

              {/* Business Impact Analysis */}
              <div
                data-chart="workplace-impact"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Insights
                    sx={{
                      marginRight: "0.5rem",
                      color: "#667eea",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Business Impact Analysis
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Projected business impact score for each solution
                </p>
                <Radar
                  data={workplacePriorityData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `${context.label}: ${context.parsed.r}/100`;
                          },
                        },
                      },
                    },
                    scales: {
                      r: {
                        beginAtZero: true,
                        max: 100,
                        ticks: {
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                        },
                        pointLabels: {
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Your Responses Tab */}
        {selectedTab === "your-responses" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "20px",
                padding: isMobile ? "1.5rem" : "2rem",
                backdropFilter: "blur(10px)",
              }}
            >
              <h3
                style={{
                  marginBottom: "1.5rem",
                  color: "#1f2937",
                  display: "flex",
                  alignItems: "center",
                  fontFamily: "var(--font-headline)",
                  fontSize: isMobile
                    ? "calc(var(--font-size-headline-mobile) * 0.8)"
                    : "calc(var(--font-size-headline) * 0.8)",
                  fontWeight: "600",
                }}
              >
                <Groups
                  sx={{
                    marginRight: "0.5rem",
                    color: "#c9a750ff",
                    fontSize: isMobile ? "1.5rem" : "2rem",
                  }}
                />
                Your Workplace Assessment Results
              </h3>
              <div
                style={{
                  display: "grid",
                  gap: "1rem",
                }}
              >
                {results.map((result, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "1rem",
                      backgroundColor: "#f8fafc",
                      borderRadius: "12px",
                      borderLeft: "4px solid #c9a750ff",
                    }}
                  >
                    <h4
                      style={{
                        color: "#1f2937",
                        marginBottom: "0.5rem",
                        fontSize: isMobile ? "0.9rem" : "1rem",
                        fontWeight: "600",
                      }}
                    >
                      {result.question}
                    </h4>
                    <p
                      style={{
                        color: "#6b7280",
                        margin: 0,
                        fontSize: isMobile ? "0.8rem" : "0.9rem",
                        lineHeight: "1.5",
                      }}
                    >
                      {result.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div
          className={animationStep >= 3 ? "fade-in" : ""}
          style={{
            display: "flex",
            justifyContent: "center",
            gap: isMobile ? "1rem" : "2rem",
            marginTop: isMobile ? "2rem" : "3rem",
            opacity: animationStep >= 3 ? 1 : 0,
            transform:
              animationStep >= 3 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-in-out",
            padding: isMobile ? "0 1rem" : "0",
          }}
        >
          <PDFExport
            results={results}
            recommendations={recommendations}
            resultsRef={resultsRef}
          />
          <button
            onClick={onRestart}
            style={{
              padding: isMobile ? "0.75rem 1.5rem" : "1rem 2rem",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
              border: "2px solid #c9a750ff",
              borderRadius: "12px",
              cursor: "pointer",
              fontSize: isMobile ? "1rem" : "1.1rem",
              fontWeight: "600",
              transition: "all 0.3s ease",
              backdropFilter: "blur(10px)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#c9a750ff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.2)";
            }}
          >
            Take Quiz Again
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkplaceResults;
